/**
 * ConsentsTab.jsx — Consent History Tab for IndividualProfile
 *
 * Shows all recorded consents, allows recording new ones and revoking existing.
 *
 * Powered by Cold Harbor Digital
 */

import React, { useState, useEffect } from "react";
import { useAuth } from "../hooks/useAuth";
import {
  onIndividualConsents, recordConsent, revokeConsent, CONSENT_TYPES,
} from "../services/intakeService";
import {
  Shield, Check, X, Plus, AlertTriangle, Clock,
} from "lucide-react";

const S = {
  card: {
    background: "rgba(15,23,42,0.7)", border: "1px solid rgba(99,179,237,0.08)",
    borderRadius: 16, padding: 16, marginBottom: 12,
  },
  label: { display: "block", fontSize: 11, fontWeight: 700, color: "#a0aec0", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 },
  select: {
    width: "100%", padding: "10px 12px", borderRadius: 10, fontSize: 14, fontWeight: 500,
    background: "rgba(15,23,42,0.6)", color: "#e2e8f0",
    border: "1px solid rgba(99,179,237,0.1)", outline: "none", appearance: "auto", boxSizing: "border-box",
  },
  input: {
    width: "100%", padding: "10px 12px", borderRadius: 10, fontSize: 14, fontWeight: 500,
    background: "rgba(15,23,42,0.6)", color: "#e2e8f0",
    border: "1px solid rgba(99,179,237,0.1)", outline: "none", boxSizing: "border-box",
  },
};

export default function ConsentsTab({ individualId, individualName }) {
  const { role } = useAuth();
  const [consents, setConsents] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [revoking, setRevoking] = useState(null); // consentId
  const [revokeReason, setRevokeReason] = useState("");
  const [saving, setSaving] = useState(false);

  const [newConsent, setNewConsent] = useState({
    consentType: "",
    signedBy: individualName || "",
    signedByRelation: "self",
  });

  useEffect(() => {
    if (!individualId) return;
    const unsub = onIndividualConsents(individualId, setConsents);
    return unsub;
  }, [individualId]);

  const canManage = ["frontdesk", "supervisor", "admin", "clinician"].includes(role);

  const activeConsents = consents.filter(c => !c.revoked);
  const revokedConsents = consents.filter(c => c.revoked);

  // Which consent types are already active
  const activeTypes = new Set(activeConsents.map(c => c.consentType));

  const formatDate = (d) => {
    if (!d) return "—";
    if (d.toDate) return d.toDate().toLocaleDateString();
    return new Date(d).toLocaleDateString();
  };

  const formatDateTime = (d) => {
    if (!d) return "—";
    if (d.toDate) {
      const dt = d.toDate();
      return `${dt.toLocaleDateString()} ${dt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
    }
    return new Date(d).toLocaleDateString();
  };

  const handleAddConsent = async () => {
    if (!newConsent.consentType) return;
    setSaving(true);
    const type = CONSENT_TYPES.find(c => c.id === newConsent.consentType);
    await recordConsent(individualId, {
      consentType: newConsent.consentType,
      consentLabel: type?.label || newConsent.consentType,
      signedBy: newConsent.signedBy,
      signedByRelation: newConsent.signedByRelation,
    });
    setSaving(false);
    setShowAddForm(false);
    setNewConsent({ consentType: "", signedBy: individualName || "", signedByRelation: "self" });
  };

  const handleRevoke = async (consentId) => {
    setSaving(true);
    await revokeConsent(consentId, revokeReason);
    setSaving(false);
    setRevoking(null);
    setRevokeReason("");
  };

  // Check which required consents are missing
  const missingRequired = CONSENT_TYPES.filter(c => c.required && !activeTypes.has(c.id));

  return (
    <div>
      {/* Missing Required Consents Warning */}
      {missingRequired.length > 0 && (
        <div style={{
          ...S.card, borderColor: "rgba(252,129,129,0.15)", background: "rgba(252,129,129,0.04)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
            <AlertTriangle size={14} style={{ color: "#fc8181" }} />
            <span style={{ fontSize: 12, fontWeight: 800, color: "#fc8181" }}>
              {missingRequired.length} Required Consent{missingRequired.length > 1 ? "s" : ""} Missing
            </span>
          </div>
          {missingRequired.map(c => (
            <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
              <X size={10} style={{ color: "#fc8181" }} />
              <span style={{ fontSize: 12, color: "#fc8181" }}>{c.label}</span>
            </div>
          ))}
        </div>
      )}

      {/* Active Consents */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Shield size={14} style={{ color: "#68d391" }} />
          <span style={{ fontSize: 13, fontWeight: 800, color: "#68d391" }}>
            Active Consents ({activeConsents.length})
          </span>
        </div>
        {canManage && (
          <button onClick={() => setShowAddForm(true)} style={{
            padding: "6px 12px", borderRadius: 8, fontSize: 11, fontWeight: 700,
            background: "rgba(104,211,145,0.08)", color: "#68d391",
            border: "1px solid rgba(104,211,145,0.15)", cursor: "pointer",
            display: "flex", alignItems: "center", gap: 4,
          }}>
            <Plus size={12} /> Record Consent
          </button>
        )}
      </div>

      {activeConsents.length === 0 ? (
        <div style={{ ...S.card, textAlign: "center", padding: "24px 16px" }}>
          <Shield size={24} style={{ color: "#2d3748", margin: "0 auto 8px" }} />
          <div style={{ fontSize: 12, color: "#4a5568" }}>No consents on record</div>
        </div>
      ) : (
        activeConsents.map(consent => {
          const typeDef = CONSENT_TYPES.find(c => c.id === consent.consentType);
          return (
            <div key={consent.id} style={{
              ...S.card, borderColor: "rgba(104,211,145,0.1)",
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                    <Check size={12} style={{ color: "#68d391" }} />
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#68d391" }}>
                      {consent.consentLabel || typeDef?.label || consent.consentType}
                    </span>
                    {typeDef?.required && (
                      <span style={{ fontSize: 8, fontWeight: 800, color: "#f6ad55", textTransform: "uppercase" }}>Required</span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: "#718096" }}>
                    Signed by {consent.signedBy || "—"} ({consent.signedByRelation || "self"})
                  </div>
                  <div style={{ fontSize: 10, color: "#4a5568", marginTop: 2 }}>
                    {formatDateTime(consent.signedAt)} · Witnessed: {consent.witnessedBy || "—"}
                  </div>
                </div>
                {canManage && revoking !== consent.id && (
                  <button onClick={() => setRevoking(consent.id)} style={{
                    padding: "4px 10px", borderRadius: 8, fontSize: 10, fontWeight: 600,
                    background: "rgba(252,129,129,0.06)", color: "#fc8181",
                    border: "1px solid rgba(252,129,129,0.1)", cursor: "pointer",
                  }}>
                    Revoke
                  </button>
                )}
              </div>

              {/* Revoke form */}
              {revoking === consent.id && (
                <div style={{ marginTop: 10, padding: 10, borderRadius: 10, background: "rgba(252,129,129,0.04)", border: "1px solid rgba(252,129,129,0.1)" }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#fc8181", marginBottom: 6 }}>Revoke this consent?</div>
                  <input style={{ ...S.input, marginBottom: 8, fontSize: 12 }} value={revokeReason}
                    onChange={e => setRevokeReason(e.target.value)} placeholder="Reason for revocation..." />
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={() => handleRevoke(consent.id)} disabled={saving} style={{
                      flex: 1, padding: "8px 0", borderRadius: 8, fontSize: 11, fontWeight: 700,
                      background: "linear-gradient(135deg, #fc8181, #e53e3e)", color: "white",
                      border: "none", cursor: "pointer",
                    }}>
                      {saving ? "Revoking..." : "Confirm Revoke"}
                    </button>
                    <button onClick={() => { setRevoking(null); setRevokeReason(""); }} style={{
                      flex: 1, padding: "8px 0", borderRadius: 8, fontSize: 11, fontWeight: 600,
                      background: "transparent", color: "#718096",
                      border: "1px solid rgba(99,179,237,0.08)", cursor: "pointer",
                    }}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })
      )}

      {/* Revoked Consents */}
      {revokedConsents.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: "#718096", marginBottom: 8 }}>
            Revoked ({revokedConsents.length})
          </div>
          {revokedConsents.map(consent => (
            <div key={consent.id} style={{ ...S.card, opacity: 0.6 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                <X size={12} style={{ color: "#718096" }} />
                <span style={{ fontSize: 13, fontWeight: 700, color: "#718096", textDecoration: "line-through" }}>
                  {consent.consentLabel || consent.consentType}
                </span>
              </div>
              <div style={{ fontSize: 10, color: "#4a5568" }}>
                Revoked {formatDateTime(consent.revokedAt)}
                {consent.revokedReason && ` — ${consent.revokedReason}`}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Consent Modal */}
      {showAddForm && (
        <div style={{ position: "fixed", inset: 0, zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.7)", padding: 20 }}
          onClick={() => setShowAddForm(false)}>
          <div style={{
            background: "linear-gradient(135deg, #0f1729, #162033)",
            border: "1px solid rgba(104,211,145,0.15)",
            borderRadius: 16, padding: 24, maxWidth: 400, width: "100%",
          }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 16, fontWeight: 800, color: "#68d391", marginBottom: 16 }}>Record Consent</div>

            <div style={{ marginBottom: 12 }}>
              <label style={S.label}>Consent Type</label>
              <select style={S.select} value={newConsent.consentType} onChange={e => setNewConsent(p => ({ ...p, consentType: e.target.value }))}>
                <option value="">Select...</option>
                {CONSENT_TYPES.filter(c => !activeTypes.has(c.id)).map(c => (
                  <option key={c.id} value={c.id}>
                    {c.label}{c.required ? " (Required)" : ""}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={S.label}>Signed By</label>
              <input style={S.input} value={newConsent.signedBy} onChange={e => setNewConsent(p => ({ ...p, signedBy: e.target.value }))} />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={S.label}>Relationship</label>
              <select style={S.select} value={newConsent.signedByRelation} onChange={e => setNewConsent(p => ({ ...p, signedByRelation: e.target.value }))}>
                <option value="self">Self</option>
                <option value="guardian">Legal Guardian</option>
                <option value="parent">Parent</option>
                <option value="poa">Power of Attorney</option>
              </select>
            </div>

            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={handleAddConsent} disabled={saving || !newConsent.consentType} style={{
                flex: 1, padding: "12px 0", borderRadius: 12, fontSize: 13, fontWeight: 800,
                background: !newConsent.consentType ? "rgba(104,211,145,0.2)" : "linear-gradient(135deg, #48bb78, #38a169)",
                color: "white", border: "none", cursor: newConsent.consentType ? "pointer" : "default",
              }}>
                {saving ? "Saving..." : "Record Consent"}
              </button>
              <button onClick={() => setShowAddForm(false)} style={{
                flex: 1, padding: "12px 0", borderRadius: 12, fontSize: 13, fontWeight: 600,
                background: "transparent", color: "#718096",
                border: "1px solid rgba(99,179,237,0.08)", cursor: "pointer",
              }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}